import React from 'react'
import type { PlayerRow } from '../types'

type WindowKey = 'season' | 'last10' | 'last5' | 'last3'

interface PlayerComparisonProps {
  players: PlayerRow[]
  window: WindowKey
  onClose: () => void
  onRemovePlayer: (playerId: number) => void
}

const STATS = [
  { key: 'minutes', label: 'Minutes', higherBetter: true },
  { key: 'goals', label: 'Goals', higherBetter: true },
  { key: 'assists', label: 'Assists', higherBetter: true },
  { key: 'shots', label: 'Shots', higherBetter: true },
  { key: 'shots_on_target', label: 'SOT', higherBetter: true },
  { key: 'yellows', label: 'Yellows', higherBetter: false },
  { key: 'reds', label: 'Reds', higherBetter: false },
  { key: 'fouls_committed', label: 'Fouls', higherBetter: false },
  { key: 'fouls_drawn', label: 'Fouls Won', higherBetter: true },
  { key: 'passes_key', label: 'Key Passes', higherBetter: true },
  { key: 'dribbles_success', label: 'Dribbles', higherBetter: true },
]

function formatStatName(key: string): string {
  return STATS.find((s) => s.key === key)?.label || key
}

function getBestClass(
  players: PlayerRow[],
  stat: string,
  currentPlayer: PlayerRow,
  window: WindowKey,
  higherBetter: boolean
): string {
  const values = players.map((p) => {
    const stats = p.windows?.[window] || {}
    return stats[stat] ?? 0
  })

  const currentStats = currentPlayer.windows?.[window] || {}
  const currentVal = currentStats[stat] ?? 0

  if (values.every((v) => v === 0)) return ''

  const bestVal = higherBetter ? Math.max(...values) : Math.min(...values.filter((v) => v > 0))

  if (currentVal === bestVal && currentVal !== 0) {
    return higherBetter ? 'best' : 'worst'
  }
  return ''
}

export function PlayerComparison({
  players,
  window,
  onClose,
  onRemovePlayer,
}: PlayerComparisonProps) {
  if (players.length < 2) {
    return null
  }

  return (
    <div className="player-comparison">
      <div className="comparison-header">
        <h3>Player Comparison</h3>
        <button className="comparison-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <table className="comparison-table">
        <thead>
          <tr>
            <th>Stat</th>
            {players.map((p) => (
              <th key={p.player_id} className="player-header">
                <span className="player-name">{p.name}</span>
                <span className="player-position">{p.position}</span>
                <button
                  className="remove-player"
                  onClick={() => onRemovePlayer(p.player_id)}
                  title="Remove from comparison"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {STATS.map((stat) => {
            const hasData = players.some((p) => {
              const stats = p.windows?.[window] || {}
              return (stats[stat.key] ?? 0) > 0
            })

            if (!hasData) return null

            return (
              <tr key={stat.key}>
                <td className="stat-label">{stat.label}</td>
                {players.map((p) => {
                  const stats = p.windows?.[window] || {}
                  const val = stats[stat.key]
                  const className = getBestClass(
                    players,
                    stat.key,
                    p,
                    window,
                    stat.higherBetter
                  )

                  return (
                    <td key={p.player_id} className={className}>
                      {val ?? '—'}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="comparison-legend">
        <span className="legend-item best">
          <span className="legend-dot" />
          Best
        </span>
        <span className="legend-item worst">
          <span className="legend-dot" />
          Worst (for negative stats)
        </span>
      </div>
    </div>
  )
}
